import AppContext from "PFApp/app_context";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useCurrentProfileUpdate } from "PFCore/hooks/queries/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { ApiRoute } from "PFCore/utilities/routes";
import { ChangeRequest, CurrentAccount, CurrentProfile, CustomType } from "PFTypes";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

type UseOnboardingReturn = {
  ready: boolean;
  stepDotsProps: any;
  currentProfile: CurrentProfile;
  currentAccount: CurrentAccount;
  suggestedProfile: CurrentProfile | null;
  customTypes: CustomType[];
  saveProfile: (data: Partial<CurrentProfile>) => Promise<CurrentProfile>;
  navigateNext: VoidFunction;
  navigateBack: VoidFunction;
  changeRequests: ChangeRequest[] | null;
};

type Step = "tos" | "cv" | "skills" | "top_skills" | "review" | "success" | "details";

export const useOnboarding = (step: Step): UseOnboardingReturn => {
  const history = useHistory();
  const {
    store: { currentAccount }
  } = useContext(AppContext);
  const { data: currentProfile } = useCurrentProfile();
  const { customTypes } = useCustomTypes();

  const { mutateAsync: saveProfile } = useCurrentProfileUpdate();

  const needsChangeRequests = !["tos", "cv", "success"].includes(step);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[] | null>(null);
  useEffect(() => {
    if (currentProfile.id && needsChangeRequests) {
      const pid = currentProfile.id;
      const path = `/api/change_requests?applied=false&diff_only=true&page=1&per_page=100&profile_id=${pid}`;
      // @ts-ignore
      // eslint-disable-next-line camelcase
      $.ajax({ api_version: 2, url: ApiRoute(path) }).then(({ entries }) => setChangeRequests(entries));
    }
  }, [currentProfile.id]);

  const [suggestedProfile, setSuggestedProfile] = useState<CurrentProfile | null>(null);
  useEffect(() => {
    const url = ApiRoute(`/api/profiles/${currentProfile.id}/suggested`);
    // @ts-ignore
    // eslint-disable-next-line camelcase
    $.ajax({ api_version: 2, url }).then(setSuggestedProfile);
  }, []);

  const ready = !!(suggestedProfile && currentProfile && (changeRequests || !needsChangeRequests));

  const stepDotsProps = useMemo(() => {
    const countedSteps = ["details", "skills", "top_skills"];
    changeRequests?.length && countedSteps.push("review");
    return {
      ready,
      stepsCount: countedSteps.length,
      currentIndex: countedSteps.indexOf(step)
    };
  }, [ready]);

  const canTos = currentAccount.customization?.show_terms_and_conditions;
  const canCv = currentAccount.cv_parser_config?.enabled;

  const allSteps = [
    canTos ? "tos" : false,
    canCv ? "cv" : false,
    "details",
    "skills",
    "top_skills",
    changeRequests?.length && "review",
    "success"
  ].filter(Boolean);

  const navigateStep = (change: number) => {
    const index = allSteps.indexOf(step);
    const path = `/onboarding/${allSteps[index + change] || allSteps[0]}`;
    history.push(path);
  };

  // avoid navigating before changeRequests are there so we don't end up in a wrong step
  const navigateNext = useCallback(() => (!needsChangeRequests || ready) && navigateStep(1), [ready]);
  const navigateBack = useCallback(() => (!needsChangeRequests || ready) && navigateStep(-1), [ready]);

  return {
    currentAccount,
    currentProfile,
    suggestedProfile,
    customTypes,
    saveProfile,
    changeRequests,
    ready,
    stepDotsProps,
    navigateNext,
    navigateBack
  };
};
